import sum from 'lodash/sum';

import {IChartDataItem} from 'common/charts/visualization/line-chart/types';
import {
  AggregatedSeries,
  AnalysisType,
  ChartPlotStyle,
  DisplayOptions,
  FlatSegment,
  InsightsBookmarkParams,
  InsightsQueryResults,
} from 'common/report/util/insights/types';
import {
  getCumulativeTimeseries,
  getDisplayOptions,
  getFlatSegments,
  getMissingZeroSegments,
  getSortConfig,
  getSortedFlatSegments,
  getSortedKeyMap,
  getTimeseriesDates,
  getTimeseriesFromTimeComparisonValues,
  getTimestampTuplesFromTimeseries,
} from 'common/report/util/insights/utils';
import {ProjectTimezone} from 'common/report/stores/project-details';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {ChartType, ValueRepresentationType} from 'common/report/constants';
import {SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {
  DisplayAnalysisType,
  DisplayPlotStyle,
  DisplayStackingType,
  LineStyle,
} from 'common/charts/visualization/line-chart/enums';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

export interface LineChartData {
  lineChartSeries: IChartDataItem[]; // sorted array of line series for use in the visualization component
  legendSegmentItems: SegmentItem[]; // sorted array of info for each segment (for use in SegmentFilterController)
  chartAnalysisType: DisplayAnalysisType;
  chartPlotStyle: DisplayPlotStyle;
  chartStackingType: DisplayStackingType;
  chartTimeUnit: TimeUnit;

  // Insights uses a special index with a stable sort order (independent from the bookmark sort order) to store selected legend segments
  legendIdToIndexMap: {[id: string]: number};
  legendIndexToId: string[];

  // easy lookups for data segment to
  segmentLookup: Map<string, FlatSegment>;
  timeComparisonSegmentLookup: Map<string, FlatSegment>;
}

// convert Insights' display options to the corresponding analysis type used in <mp-line-chart>
function getLineChartAnalysisType(displayOptions: DisplayOptions): DisplayAnalysisType {
  if (displayOptions.analysis === AnalysisType.Logarithmic) {
    return DisplayAnalysisType.Logarithmic;
  }
  return DisplayAnalysisType.Linear;
}

// convert Insights' display options to the corresponding plot style used in <mp-line-chart>
function getLineChartPlotStyle(displayOptions: DisplayOptions): DisplayPlotStyle {
  if (displayOptions.plotStyle === ChartPlotStyle.Stacked) {
    return DisplayPlotStyle.Stacked;
  }
  return DisplayPlotStyle.Standard;
}

function getLineChartStackingType(displayOptions: DisplayOptions): DisplayStackingType {
  if (displayOptions.value === ValueRepresentationType.Relative) {
    return DisplayStackingType.Percent;
  }
  return DisplayStackingType.Normal;
}

function getLineSeries(segment: FlatSegment, colorIdx: number, projectTimezone: ProjectTimezone): IChartDataItem {
  return {
    data: getTimestampTuplesFromTimeseries(segment.value, projectTimezone),
    name: segment.key,
    color: getChartSegmentColorCombo(colorIdx).primary,
  };
}

function mergeSegmentValues(
  mainSegments: Map<string, FlatSegment>,
  timeComparisonSegments: Map<string, FlatSegment>,
): FlatSegment[] {
  return Array.from(mainSegments.values(), (segment) => {
    if (timeComparisonSegments.has(segment.key)) {
      const timeComparisonSegment = timeComparisonSegments.get(segment.key);
      return {
        ...segment,
        value: {...segment.value, ...timeComparisonSegment.value},
      };
    }
    return segment;
  });
}

export function getLineChartData(
  projectTimezone: ProjectTimezone,
  bookmarkParams: InsightsBookmarkParams,
  results: InsightsQueryResults,
): LineChartData {
  const showTimeComparison =
    !!results.time_comparison && bookmarkParams.displayOptions.plotStyle !== ChartPlotStyle.Stacked;

  // flatten results into convenient segments
  const segments = getFlatSegments(results.headers, results.series);
  let timeComparisonSegments: FlatSegment[] = [];

  // Insights API results typically omit segments that have a value of zero for the whole date range
  // which means `results.series` & `results.time_comparison.series` can contain a differing number of segments.
  // Resurrect these omitted segments as zero-value segments so both series match again.
  if (showTimeComparison) {
    timeComparisonSegments = getFlatSegments(results.headers, results.time_comparison.series);
    const timeseriesDates = getTimeseriesDates(results);

    segments.push(...getMissingZeroSegments(timeComparisonSegments, segments, timeseriesDates.main));
    timeComparisonSegments.push(
      ...getMissingZeroSegments(segments, timeComparisonSegments, timeseriesDates.timeComparison),
    );
  }

  // transform values according to bookmark display options
  const displayOptions = getDisplayOptions(bookmarkParams.sections, bookmarkParams.displayOptions);
  if (displayOptions.analysis === AnalysisType.Cumulative) {
    [...segments, ...timeComparisonSegments].forEach((segment) => {
      segment.value = getCumulativeTimeseries(segment.value);
    });
  }

  // create lookups based on segment key, useful for correlating a main segment with a corresponding time comparison segment
  const segmentLookup = new Map(segments.map((segment) => [segment.key, segment]));
  const timeComparisonSegmentLookup = new Map(timeComparisonSegments.map((segment) => [segment.key, segment]));

  // merge segment values to include time comparison values when sorting
  const mergedValueSegments = mergeSegmentValues(segmentLookup, timeComparisonSegmentLookup);

  // sort segments
  const lineSortConfig = getSortConfig(results.headers, bookmarkParams.sorting)[ChartType.Line];
  const sortedSegmentKeys = getSortedFlatSegments(mergedValueSegments, lineSortConfig).map((s) => s.key);

  // generate line chart series & legend info from segments
  const lineChartSeries: IChartDataItem[] = [];
  const legendSegmentItems: SegmentItem[] = [];
  sortedSegmentKeys.forEach((segmentKey, idx) => {
    const segment = segmentLookup.get(segmentKey);
    const mainSeries = getLineSeries(segment, idx, projectTimezone);
    lineChartSeries.push(mainSeries);

    const timeComparisonSegment = timeComparisonSegmentLookup.get(segmentKey);
    if (timeComparisonSegment) {
      const compositeTimeseries = getTimeseriesFromTimeComparisonValues(segment.value, timeComparisonSegment.value);
      const compositeSegment: FlatSegment = {...timeComparisonSegment, value: compositeTimeseries};
      lineChartSeries.push({
        ...getLineSeries(compositeSegment, idx, projectTimezone),
        dashStyle: LineStyle.Comparison,
        incompleteLength: 0,
        isComparisonItem: true,
      });
    }

    legendSegmentItems.push({id: segmentKey, label: segment.displayName, color: mainSeries.color});
  });

  // generate special indices used by Insights to store selected legend segments
  const aggregatedSeries: AggregatedSeries = segments.reduce((aggregate: AggregatedSeries, segment) => {
    aggregate[segment.key] = {
      paths: [segment.path],
      total: sum(Object.values(segment.value).map((metricValue) => Math.abs(metricValue))),
    };
    return aggregate;
  }, {});
  const legendIdToIndexMap = getSortedKeyMap(aggregatedSeries);
  const legendIndexToId: string[] = [];
  Object.keys(legendIdToIndexMap).forEach((id) => (legendIndexToId[legendIdToIndexMap[id]] = id));

  // visualization component display options
  const chartAnalysisType = getLineChartAnalysisType(displayOptions);
  const chartPlotStyle = getLineChartPlotStyle(displayOptions);
  const chartStackingType = getLineChartStackingType(displayOptions);
  const chartTimeUnit = displayOptions.timeUnit ?? TimeUnit.Day;

  return {
    lineChartSeries,
    legendSegmentItems,
    chartAnalysisType,
    chartPlotStyle,
    chartStackingType,
    segmentLookup,
    timeComparisonSegmentLookup,
    chartTimeUnit,
    legendIdToIndexMap,
    legendIndexToId,
  };
}
